// Slow-reveal typewriter for short emphasis phrases (e.g., the trail
// half of the voice signature). Calm by design: no caret, no blink.
//
// Runs once per session via sessionStorage so subsequent visits to the
// page show the full text instantly. Honors prefers-reduced-motion.

import { useEffect, useState } from "react";

export type TypewriterProps = {
  text: string;
  /** Milliseconds per character. Default 30ms. */
  speed?: number;
  /** Delay before typing starts. Useful when waiting for a parent
   *  fade-in to settle. Default 0ms. */
  startDelay?: number;
  /** sessionStorage key. If set and already 1, render full text instantly. */
  sessionKey?: string;
};

function shouldSkip(sessionKey?: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return true;
    if (sessionKey && window.sessionStorage.getItem(sessionKey) === "1") return true;
  } catch {
    // sessionStorage can throw under some privacy modes; fail open.
  }
  return false;
}

export function Typewriter({ text, speed = 30, startDelay = 0, sessionKey }: TypewriterProps) {
  const [count, setCount] = useState<number>(() => (shouldSkip(sessionKey) ? text.length : 0));
  const [started, setStarted] = useState<boolean>(() => shouldSkip(sessionKey) || startDelay <= 0);

  useEffect(() => {
    if (started) return;
    const id = window.setTimeout(() => setStarted(true), startDelay);
    return () => window.clearTimeout(id);
  }, [started, startDelay]);

  useEffect(() => {
    if (!started) return;
    if (count >= text.length) {
      if (sessionKey) {
        try {
          window.sessionStorage.setItem(sessionKey, "1");
        } catch {}
      }
      return;
    }
    const id = window.setTimeout(() => setCount((c) => c + 1), speed);
    return () => window.clearTimeout(id);
  }, [count, text.length, speed, sessionKey, started]);

  return <>{text.slice(0, count)}</>;
}
